import axios from 'axios';
import { QueryIntent } from './queryAnalyzerLLM';

export interface ColorTheme {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  surface: string;
  text: string;
  textSecondary: string;
  
  // Semantic colors
  error: string;
  success: string;
  warning: string;
  highlight: string;
  
  // UI colors
  border: string;
  surfaceHover: string;
  
  conceptColors: {
    theory: string;
    canon: string;
    ship: string;
    controversy: string;
    meme: string;
    character: string;
  };
}

const OPENAI_API_URL = 'https://api.openai.com/v1/chat/completions';
const API_KEY = process.env.REACT_APP_OPENAI_KEY;

// Default themes for different intent types
const defaultThemes: Record<string, Partial<ColorTheme>> = {
  THEORY: {
    primary: '#6B46C1', // Purple for mystery
    secondary: '#9333EA',
    accent: '#C084FC',
    background: '#1A0B2E',
    surface: '#2D1B4E',
  },
  SHIP: {
    primary: '#EC4899', // Pink for romance
    secondary: '#F472B6',
    accent: '#FBCFE8',
    background: '#2D0A1F',
    surface: '#4A1937',
  },
  CONTROVERSY: {
    primary: '#DC2626', // Red for drama
    secondary: '#EF4444',
    accent: '#FCA5A5',
    background: '#2A0E0E',
    surface: '#451A1A',
  },
  MEME: {
    primary: '#10B981', // Green for humor
    secondary: '#34D399',
    accent: '#86EFAC',
    background: '#0A2818',
    surface: '#164430',
  },
  CANON: {
    primary: '#3B82F6', // Blue for official
    secondary: '#60A5FA',
    accent: '#93BBFC',
    background: '#0A1929',
    surface: '#1E3A5F',
  },
  CHARACTER: {
    primary: '#F59E0B', // Amber for character
    secondary: '#FBBF24',
    accent: '#FDE68A',
    background: '#2D1F0A',
    surface: '#4A3319',
  }
};

export async function generateTheme(query: string, intent: QueryIntent): Promise<ColorTheme> {
  try {
    const prompt = `Generate a color scheme for a web interface exploring: "${query}"

Context: This is a ${intent.type} query${intent.fandom ? ` about ${intent.fandom}` : ''}.

Provide a cohesive color palette that matches the mood and topic. Consider:
- For theories/mysteries: Deep purples, dark blues, mysterious tones
- For ships/romance: Warm pinks, soft reds, romantic hues
- For controversies: Bold reds, dark oranges, dramatic colors
- For memes/humor: Bright greens, playful colors, vibrant tones
- For canon/lore: Classic blues, authoritative colors
- For characters: Character-specific colors if applicable

Return ONLY a JSON object with hex colors:
{
  "primary": "#...",
  "secondary": "#...",
  "accent": "#...",
  "background": "#...",
  "surface": "#...",
  "text": "#...",
  "textSecondary": "#..."
}`;

    const response = await axios.post(
      OPENAI_API_URL,
      {
        model: 'gpt-4-turbo-preview',
        messages: [
          {
            role: 'system',
            content: 'You are a color theme generator. Return only valid JSON with hex color codes.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: 0.9,
        max_tokens: 200
      },
      {
        headers: {
          'Authorization': `Bearer ${API_KEY}`,
          'Content-Type': 'application/json'
        }
      }
    );

    const generatedColors = JSON.parse(response.data.choices[0].message.content);
    
    // Merge with concept-specific colors and ensure all required fields
    return {
      ...generatedColors,
      // Ensure semantic colors exist (fallback if not generated)
      error: generatedColors.error || '#ef4444',
      success: generatedColors.success || '#10b981',
      warning: generatedColors.warning || '#f59e0b',
      highlight: generatedColors.highlight || '#60a5fa',
      // Ensure UI colors exist (fallback if not generated)
      border: generatedColors.border || '#334155',
      surfaceHover: generatedColors.surfaceHover || '#334155',
      conceptColors: {
        theory: '#9333EA',
        canon: '#3B82F6',
        ship: '#EC4899',
        controversy: '#DC2626',
        meme: '#10B981',
        character: '#F59E0B'
      }
    };
  } catch (error) {
    console.error('Theme generation failed, using defaults:', error);
    
    // Fallback to default theme based on intent
    const defaultTheme = defaultThemes[intent.type] || defaultThemes.CANON;
    return {
      primary: defaultTheme.primary || '#3B82F6',
      secondary: defaultTheme.secondary || '#60A5FA',
      accent: defaultTheme.accent || '#93BBFC',
      background: defaultTheme.background || '#0A1929',
      surface: defaultTheme.surface || '#1E3A5F',
      text: '#FFFFFF',
      textSecondary: '#A0AEC0',
      
      // Semantic colors
      error: '#ef4444',
      success: '#10b981',
      warning: '#f59e0b',
      highlight: '#60a5fa',
      
      // UI colors
      border: '#334155',
      surfaceHover: '#334155',
      
      conceptColors: {
        theory: '#9333EA',
        canon: '#3B82F6',
        ship: '#EC4899',
        controversy: '#DC2626',
        meme: '#10B981',
        character: '#F59E0B'
      }
    };
  }
}

// Apply theme to CSS variables
export function applyTheme(theme: ColorTheme) {
  const root = document.documentElement;
  
  // Helper functions
  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };
  
  const hexToRgb = (hex: string) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `${r}, ${g}, ${b}`;
  };
  
  // Set main colors
  root.style.setProperty('--color-primary', theme.primary);
  root.style.setProperty('--color-secondary', theme.secondary);
  root.style.setProperty('--color-accent', theme.accent);
  root.style.setProperty('--color-background', theme.background);
  root.style.setProperty('--color-surface', theme.surface);
  root.style.setProperty('--color-text', theme.text);
  root.style.setProperty('--color-text-secondary', theme.textSecondary);
  
  // Set UI colors
  root.style.setProperty('--color-border', theme.border);
  root.style.setProperty('--color-surface-hover', theme.surfaceHover);
  
  // Set RGB versions for glassmorphism
  root.style.setProperty('--color-primary-rgb', hexToRgb(theme.primary));
  root.style.setProperty('--color-surface-rgb', hexToRgb(theme.surface));
  root.style.setProperty('--color-border-rgb', hexToRgb(theme.border));
  
  // Set semantic colors
  root.style.setProperty('--color-error', theme.error);
  root.style.setProperty('--color-success', theme.success);
  root.style.setProperty('--color-warning', theme.warning);
  root.style.setProperty('--color-highlight', theme.highlight);
  
  // Set derived colors for gradients and light versions
  root.style.setProperty('--color-primary-light', hexToRgba(theme.primary, 0.1));
  root.style.setProperty('--color-primary-shadow', hexToRgba(theme.primary, 0.3));
  root.style.setProperty('--shadow-glow', `0 0 16px ${hexToRgba(theme.primary, 0.2)}`);
  root.style.setProperty('--color-success-light', hexToRgba(theme.success, 0.2));
  root.style.setProperty('--color-success-lighter', hexToRgba(theme.success, 0.1));
  root.style.setProperty('--color-success-border', hexToRgba(theme.success, 0.4));
  root.style.setProperty('--color-highlight-light', hexToRgba(theme.highlight, 0.15));
  root.style.setProperty('--color-highlight-lighter', hexToRgba(theme.highlight, 0.08));
  root.style.setProperty('--color-error-light', hexToRgba(theme.error, 0.1));
  root.style.setProperty('--color-error-border', hexToRgba(theme.error, 0.2));
  
  // Set concept colors
  Object.entries(theme.conceptColors).forEach(([type, color]) => {
    root.style.setProperty(`--color-concept-${type}`, color);
  });
}